import React, { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileUp, Sparkles } from "lucide-react";

const empty = { title: "", company_id: "", type: "license", issue_date: "", expiry_date: "", responsible_person: "", counterpart: "", physical_location: "", status: "active", notes: "", file: null };

export default function DocumentFormModal({ open, onOpenChange, doc, companies }) {
  const { t } = useI18n();
  const { isAdmin, perms } = useAuth();
  const qc = useQueryClient();
  const editing = doc?.id || null;

  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aiFile, setAiFile] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAiFile(null);
    if (doc) {
      setForm({
        title: doc.title, company_id: doc.company_id, type: doc.type,
        issue_date: doc.issue_date || "", expiry_date: doc.expiry_date || "",
        responsible_person: doc.responsible_person || "", counterpart: doc.counterpart || "", physical_location: doc.physical_location || "", status: doc.status || "active",
        notes: doc.notes || "", file: doc.file || null,
      });
    } else {
      setForm(empty);
    }
  }, [open, doc]);

  const saveMut = useMutation({
    mutationFn: async (payload) => {
      if (editing) return (await api.put(`/documents/${editing}`, payload)).data;
      return (await api.post("/documents", payload)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["documents"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      onOpenChange(false);
      toast.success(t("save") + " ✓");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, file: data }));
      setAiFile(f);
      toast.success(f.name);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const runAiExtract = async () => {
    if (!aiFile) return;
    setAiLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", aiFile);
      const { data } = await api.post("/documents/ai-extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fields = data.fields || {};
      setForm((s) => {
        const next = { ...s };
        ["title", "issue_date", "expiry_date", "counterpart", "responsible_person", "notes"].forEach((k) => {
          if (fields[k]) next[k] = fields[k];
        });
        if (fields.type) next.type = fields.type;
        return next;
      });
      toast.success(t("aiDone"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setAiLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.company_id) { toast.error(t("required")); return; }
    setSaving(true);
    const payload = { ...form, issue_date: form.issue_date || null, expiry_date: form.expiry_date || null };
    await saveMut.mutateAsync(payload).catch(() => {});
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="document-modal">
        <DialogHeader><DialogTitle className="font-display">{editing ? t("editDocument") : t("addDocument")}</DialogTitle><DialogDescription>{t("documentsSub")}</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label={t("title")}><Input data-testid="doc-title-input" value={form.title} onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))} required /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("company")}>
              <Select value={form.company_id} onValueChange={(v) => setForm((s) => ({ ...s, company_id: v }))}>
                <SelectTrigger data-testid="doc-company-select"><SelectValue placeholder={t("company")} /></SelectTrigger>
                <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label={t("type")}>
              <Select value={form.type} onValueChange={(v) => setForm((s) => ({ ...s, type: v }))}>
                <SelectTrigger data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(isAdmin || !perms.doc_types?.length ? ["statutory", "license", "contract", "litigation", "resolutions", "other"] : perms.doc_types.filter((x) => x !== "correspondence")).map((tp) => (
                    <SelectItem key={tp} value={tp}>{t(tp)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("issueDate")}><Input type="date" data-testid="doc-issue-input" value={form.issue_date} onChange={(e) => setForm((s) => ({ ...s, issue_date: e.target.value }))} /></Field>
            <Field label={t("expiryDate")}><Input type="date" data-testid="doc-expiry-input" value={form.expiry_date} onChange={(e) => setForm((s) => ({ ...s, expiry_date: e.target.value }))} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("responsible")}><Input data-testid="doc-responsible-input" value={form.responsible_person} onChange={(e) => setForm((s) => ({ ...s, responsible_person: e.target.value }))} /></Field>
            <Field label={t("counterpart")}><Input data-testid="doc-counterpart-input" value={form.counterpart} onChange={(e) => setForm((s) => ({ ...s, counterpart: e.target.value }))} /></Field>
          </div>
          <Field label={t("physicalLocation")}><Input data-testid="doc-physical-location-input" value={form.physical_location} onChange={(e) => setForm((s) => ({ ...s, physical_location: e.target.value }))} placeholder={t("physicalLocationPlaceholder")} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("status")}>
              <Select value={form.status} onValueChange={(v) => setForm((s) => ({ ...s, status: v }))}>
                <SelectTrigger data-testid="doc-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">{t("active")}</SelectItem>
                  <SelectItem value="pending">{t("pending")}</SelectItem>
                  <SelectItem value="renewed">{t("renewed")}</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field label={t("notes")}><Textarea data-testid="doc-notes-input" value={form.notes} onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))} rows={2} /></Field>
          <Field label={t("attachment")}>
            <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed px-3 py-2.5 text-sm transition-colors hover:border-renco-gold hover:bg-secondary/40" data-testid="doc-file-label">
              {uploading ? <span className="text-muted-foreground">…</span> : <FileUp className="h-4 w-4 text-renco-gold" />}
              <span className="truncate">{form.file ? form.file.original_filename : t("uploadFile")}</span>
              <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx" onChange={handleUpload} data-testid="doc-file-input" />
            </label>
            {aiFile && (
              <div className="mt-2 flex items-center gap-2">
                <Button type="button" size="sm" variant="outline" onClick={runAiExtract} disabled={aiLoading} data-testid="ai-autofill-button" className="gap-2 border-horizonte-orange/40 text-horizonte-orange hover:bg-horizonte-orange/10">
                  <Sparkles className={`h-4 w-4 ${aiLoading ? "animate-pulse" : ""}`} />
                  {aiLoading ? t("aiRunning") : t("aiAutofill")}
                </Button>
                <span className="text-xs text-muted-foreground">{t("aiHint")}</span>
              </div>
            )}
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="doc-cancel-button">{t("cancel")}</Button>
            <Button type="submit" disabled={saving || uploading} data-testid="doc-save-button" className="bg-renco-navy text-white hover:bg-renco-slate">{saving ? t("saving") : t("save")}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return <div className="space-y-1.5"><Label>{label}</Label>{children}</div>;
}
